import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc, setDoc, collection, query, where, onSnapshot, addDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";
import { instructionalWeek } from "../lib/pace.js";

const pad = (n) => String(n).padStart(2, "0");

export default function Learn() {
  const s = useSession();
  const [cohort, setCohort] = useState(null);
  const [viewWeek, setViewWeek] = useState(null);
  const [content, setContent] = useState(null);
  const [note, setNote] = useState(null);      // existing note doc
  const [noteText, setNoteText] = useState("");
  const [progress, setProgress] = useState({});
  const [recUrl, setRecUrl] = useState("");
  const [mod, setMod] = useState(null);
  const [modOpen, setModOpen] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!s.profile?.cohortId) return;
    getDoc(doc(db, "cohorts", s.profile.cohortId)).then((d) => d.exists() && setCohort(d.data()));
  }, [s.profile?.cohortId]);

  const now = useMemo(() => instructionalWeek(cohort?.startDate), [cohort]);
  const current = viewWeek ?? now.week;
  // Cohort pacing locks the future for residents. Staff see everything: faculty
  // prepare weeks before the cohort reaches them.
  const locked = current > now.week && !s.isStaff;

  useEffect(() => {
    getDoc(doc(db, "curriculum", `week-${pad(current)}`)).then((d) => {
      setContent(d.exists() ? d.data() : null);
      setRecUrl(d.exists() ? d.data().seminar?.recordingUrl ?? "" : "");
    });
  }, [current]);

  // The module frame above this week — matriculation is module-0, then 4-week blocks.
  useEffect(() => {
    const id = current === 0 ? "module-0" : `module-${Math.ceil(current / 4)}`;
    getDoc(doc(db, "modules", id))
      .then((d) => setMod(d.exists() ? d.data() : null))
      .catch(() => setMod(null));
  }, [current]);

  useEffect(() => {
    if (!s.user) return;
    const q = query(collection(db, "notes"), where("residentUid", "==", s.user.uid), where("week", "==", current));
    return onSnapshot(q, (snap) => {
      const d = snap.docs[0];
      setNote(d ? { id: d.id, ...d.data() } : null);
      setNoteText(d ? d.data().text : "");
    });
  }, [s.user, current]);

  useEffect(() => {
    if (!s.user) return;
    return onSnapshot(doc(db, "progress", s.user.uid), (d) => setProgress(d.exists() ? d.data() : {}));
  }, [s.user]);

  const weekKey = `w${current}`;
  const wp = progress[weekKey] ?? { labs: {}, reading: false };

  const saveProgress = async (patch) => {
    await setDoc(doc(db, "progress", s.user.uid), { [weekKey]: { ...wp, ...patch } }, { merge: true });
  };

  const fileNote = async () => {
    if (noteText.trim().length < 81) return setMsg("A position note is at least a solid paragraph — commit to a claim.");
    setMsg(null);
    if (note) {
      await updateDoc(doc(db, "notes", note.id), { text: noteText.trim(), updatedAt: serverTimestamp() });
    } else {
      await addDoc(collection(db, "notes"), {
        residentUid: s.user.uid, cohortId: s.profile?.cohortId ?? null, podId: s.profile?.podId ?? null,
        clusterId: s.profile?.clusterId ?? null, week: current, text: noteText.trim(), createdAt: serverTimestamp(),
      });
    }
    setMsg("Filed. You can revise it until the seminar.");
  };

  const saveRecording = async () => {
    await updateDoc(doc(db, "curriculum", `week-${pad(current)}`), { "seminar.recordingUrl": recUrl.trim() });
    setMsg("Recording link saved for the cohort.");
  };

  if (!content) {
    return (
      <div style={{ maxWidth: 780 }}>
        <Eyebrow>Learn</Eyebrow>
        <h1 style={{ fontSize: 24, marginTop: 4 }}>No curriculum loaded</h1>
        <p style={{ color: "var(--graphite)" }}>
          If seeding is done and this persists, tell your Studio Director — the curriculum collection may not be deployed yet.
        </p>
      </div>
    );
  }

  const labsDone = content.labs.filter((l) => wp.labs?.[l.id]).length;

  return (
    <div style={{ maxWidth: 840 }}>
      {/* week picker */}
      <div className="row wrapflex" style={{ gap: 4, marginBottom: 14 }}>
        {Array.from({ length: 25 }, (_, i) => i).map((w) => (
          <button key={w} onClick={() => setViewWeek(w)}
            className="mono"
            disabled={w > now.week && !s.isStaff}
            title={w > now.week && !s.isStaff ? "Opens with the cohort — this programme moves together" : `Week ${w}`}
            style={{
              fontSize: 11, padding: "5px 7px",
              border: `1px solid ${w === current ? "var(--ink)" : "var(--rule)"}`,
              background: w === current ? "var(--ink)" : (w > now.week && !s.isStaff) ? "transparent" : (progress[`w${w}`]?.reading ? "var(--card)" : "#fff"),
              color: w === current ? "var(--paper)" : (w > now.week && !s.isStaff) ? "var(--rule)" : "var(--ink)",
              cursor: (w > now.week && !s.isStaff) ? "not-allowed" : "pointer",
            }}>
            {w === 0 ? "M" : w}
          </button>
        ))}
      </div>

      {now.consolidation && current === now.week && (
        <div className="card" style={{ marginBottom: 14, borderColor: "var(--verdigris)" }}>
          <strong>Consolidation week.</strong> No new content by design. Catch up, resubmit, rest — the slack is part of the architecture, not a gap in it.
        </div>
      )}

      <Eyebrow>{content.month} · {content.week === 0 ? "Matriculation" : `Instructional week ${content.week}`}</Eyebrow>

      {mod && (
        <div className="card" style={{ marginTop: 10, borderLeft: "3px solid var(--ink)" }}>
          <div className="row between wrapflex">
            <div>
              <Eyebrow>About this module</Eyebrow>
              <strong style={{ fontSize: 15 }}>{mod.title}</strong>
              <p style={{ fontSize: 14, margin: "4px 0 0", fontStyle: "italic" }}>{mod.question}</p>
            </div>
            <button className="btn ghost" onClick={() => setModOpen(!modOpen)}>{modOpen ? "Close" : "Read"}</button>
          </div>
          {modOpen && (
            <div style={{ marginTop: 10, borderTop: "1px solid var(--rule)", paddingTop: 10 }}>
              <p style={{ fontSize: 14 }}>{mod.intro}</p>
              <Eyebrow>By this module's end, you can show</Eyebrow>
              <ul style={{ paddingLeft: 18, fontSize: 14, marginTop: 6 }}>
                {(mod.outcomes ?? []).map((o, i) => <li key={i} style={{ marginBottom: 3 }}>{o}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <h1 style={{ fontSize: 26, marginTop: 4 }}>{content.title}</h1>

      <div className="card" style={{ margin: "14px 0", borderLeft: "3px solid var(--ink)" }}>
        <Eyebrow>The principle this week</Eyebrow>
        <p style={{ fontSize: 17, marginTop: 6, fontStyle: "italic" }}>{content.principle}</p>
      </div>

      {/* reading + position note */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row between wrapflex">
          <Eyebrow>Canon reading</Eyebrow>
          <label style={{ fontSize: 13, display: "flex", gap: 6 }}>
            <input type="checkbox" checked={!!wp.reading} onChange={(e) => saveProgress({ reading: e.target.checked })} />
            Read
          </label>
        </div>
        <p style={{ marginTop: 6 }}><strong>{content.reading.source}</strong> — {content.reading.chapters}</p>

        <div style={{ marginTop: 12, padding: 12, background: "#fff", border: "2px solid var(--madder)" }}>
          <Eyebrow style={{ color: "var(--madder)" }}>Position note · Tier 0 · due before seminar</Eyebrow>
          <p style={{ fontSize: 14, marginTop: 6 }}>{content.reading.notePrompt}</p>
          <textarea rows={5} value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="150 words, your own hand, no AI. Commit to a claim." style={{ marginTop: 8 }} />
          <div className="row between" style={{ marginTop: 6 }}>
            <span className="mono" style={{ fontSize: 12, color: "var(--graphite)" }}>
              {noteText.trim() ? noteText.trim().split(/\s+/).length : 0} words {note && "· filed"}
            </span>
            <button className="btn" onClick={fileNote}>{note ? "Revise note" : "File note"}</button>
          </div>
        </div>
      </div>

      {/* seminar */}
      <div className="card" style={{ marginBottom: 14 }}>
        <Eyebrow>Seminar · {content.seminar.title}</Eyebrow>
        <p style={{ fontSize: 16, marginTop: 6 }}><em>{content.seminar.question}</em></p>
        {(content.seminar.body?.length ? content.seminar.body : [content.seminar.synopsis]).map((par, i) => (
          <p key={i} style={{ fontSize: 15, marginTop: 8, lineHeight: 1.55 }}>{par}</p>
        ))}
        {content.seminar.recordingUrl ? (
          <p style={{ marginTop: 8 }}><a href={content.seminar.recordingUrl} target="_blank" rel="noreferrer">Watch the recording</a></p>
        ) : (
          <p style={{ marginTop: 8, fontSize: 13, color: "var(--graphite)" }}>Recording appears here after the live session.</p>
        )}
        {s.isSenior && (
          <div className="row" style={{ marginTop: 8, gap: 8 }}>
            <input type="text" placeholder="Recording URL (senior staff only)" value={recUrl} onChange={(e) => setRecUrl(e.target.value)} />
            <button className="btn ghost" onClick={saveRecording}>Save</button>
          </div>
        )}
      </div>

      {/* labs */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row between">
          <Eyebrow>Lab · tool and technique drills</Eyebrow>
          <span className="mono" style={{ fontSize: 12 }}>{labsDone}/{content.labs.length}</span>
        </div>
        {content.labs.map((l) => (
          <label key={l.id} className="crit" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <input type="checkbox" checked={!!wp.labs?.[l.id]} style={{ marginTop: 4 }}
              onChange={(e) => saveProgress({ labs: { ...wp.labs, [l.id]: e.target.checked } })} />
            <span><strong>{l.title}.</strong> {l.task}</span>
          </label>
        ))}
        <p style={{ fontSize: 12, color: "var(--graphite)", marginTop: 8 }}>
          Labs are self-attested and unlimited-attempt. They exist to make you fluent, not to be graded — the brief is where it counts.
        </p>
      </div>

      {content.critFocus && (
        <div className="card" style={{ margin: "14px 0", borderColor: "var(--indigo)" }}>
          <Eyebrow style={{ color: "var(--indigo)" }}>The crit, this week</Eyebrow>
          <p style={{ fontSize: 14, marginTop: 6 }}>{content.critFocus}</p>
        </div>
      )}

      <div className="row wrapflex" style={{ gap: 10 }}>
        <Link to="/hunt" className="btn ghost" style={{ textDecoration: "none", display: "inline-block" }}>
          This month's Slop Hunt →
        </Link>
        <Link to={`/brief/A${pad(content.week === 0 ? 0 : content.week)}`} className="btn" style={{ textDecoration: "none", display: "inline-block" }}>
          This week's brief → A{pad(content.week === 0 ? 0 : content.week)}
        </Link>
      </div>

      {msg && <div className="card" style={{ marginTop: 12 }}>{msg}</div>}
      {locked && <div className="warn" style={{ marginTop: 12 }}>Future weeks open with the cohort. This programme moves together — that is what makes the crit worth attending.</div>}
    </div>
  );
}
